import React, { useEffect, useState, useContext } from "react";
import * as XLSX from "xlsx";
import {
  CContainer, CCard, CRow, CCol, CButton, CCardBody, CTable,
  CTableHead, CTableHeaderCell, CTableBody, CTableRow, CTableDataCell,
  CBadge, CCardHeader, CSpinner, CProgress, CProgressBar,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CFormLabel, CFormInput, CFormTextarea
} from "@coreui/react";

import CIcon from "@coreui/icons-react";
import {
  cilBuilding, cilUser, cilMap, cilChart, cilCloudDownload,
  cilLocationPin, cilPencil, cilBed
} from "@coreui/icons";
import { toast } from "react-toastify";

// * APIs & Context
import { updateWardenProfile, getHotelDetailsById } from "src/api/services/Hotel";
import AuthContext from 'src/helpers/AuthContext';
import { SERVER_URL } from "src/config/config";

export default function WardenManageHostel() {

  // --- 1. GET USER & ID ---
  const { user } = useContext(AuthContext);
  // Fallback 58 is ONLY for testing if backend doesn't send ID
  const wardenHostelId = user?.hostelId || user?.hostel_id;

  // --- STATES ---
  const [hotelData, setHotelData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Edit Modal States
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({
    contact: "", city: "", address: "", description: "", mapLink: "", image: null
  });

  // --- 2. FETCH DATA ---
  useEffect(() => {
    if (wardenHostelId) {
      fetchHostelDetails();
    } else {
      setIsLoading(false);
    }
  }, [wardenHostelId]);

  const fetchHostelDetails = async () => {
    try {
      setIsLoading(true);
      const res = await getHotelDetailsById(wardenHostelId);

      // Handle Data Structure (Backends can vary)
      let finalData = { hotel: null, roomList: [] };

      if (res?.hotel) {
        finalData = { hotel: res.hotel, roomList: res.roomList || [] };
      } else if (res?.data?.hotel) {
        finalData = { hotel: res.data.hotel, roomList: res.data.roomList || [] };
      } else {
        // Fallback
        finalData = { hotel: res, roomList: [] };
      }

      setHotelData(finalData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load details");
    } finally {
      setIsLoading(false);
    }
  };

  // --- 3. HELPER FUNCTIONS (From View.jsx) ---
  const calculateOccupancy = (room) => {
    const max = parseInt(room.maxCapcity || room.totalBeds) || 0;
    const available = room.available !== undefined ? parseInt(room.available) : max;
    // New API might send 'bookedRooms' directly
    const occupied = room.bookedRooms !== undefined ? room.bookedRooms : (max - available);

    if (max === 0) return 0;
    return Math.round((occupied / max) * 100);
  };

  const calculateOverallOccupancy = () => {
    if (!hotelData?.roomList || hotelData.roomList.length === 0) return 0;

    let totalMax = 0;
    let totalOccupied = 0;

    hotelData.roomList.forEach(room => {
      const max = parseInt(room.maxCapcity || room.totalBeds) || 0;
      const available = room.available !== undefined ? parseInt(room.available) : max;
      const occupied = room.bookedRooms !== undefined ? room.bookedRooms : (max - available);

      totalMax += max;
      totalOccupied += occupied;
    });

    if (totalMax === 0) return 0;
    return Math.round((totalOccupied / totalMax) * 100);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "N/A";

    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{5})(\d{5})/, "$1 $2");
    }

    return phone;
  };

  const getOccupancyBadgeColor = (percentage) => {
    if (percentage < 30) return "success"; // Low occupancy (Good availability)
    if (percentage < 80) return "warning"; // Medium
    return "danger"; // High occupancy (Full)
  };

  const exportToExcel = (data, fileName) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Room Details");
    XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // --- 4. EDIT HANDLERS ---
  const handleEdit = () => {
    if (!hotelData?.hotel) return;
    const h = hotelData.hotel;
    setFormData({
      contact: h.pocContact || "",
      city: h.city || "",
      address: h.address || "",
      description: h.description || "",
      mapLink: h.mapLink || "",
      image: null
    });
    setVisible(true);
  };


  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image") {
      setFormData({ ...formData, image: files[0] });

    }
    else if (name === "contact") {
      // Sirf Numbers allow karein (No alphabets/symbols)
      const numericValue = value.replace(/[^0-9]/g, "");

      // Sirf tab update karein agar length 10 ya usse kam ho
      if (numericValue.length <= 10) {
        setFormData({ ...formData, [name]: numericValue });
      }
    }

    else {
      setFormData({ ...formData, [name]: value });
    }
  };



  const handleSubmit = async () => {
    const payload = new FormData();


    payload.append("hotelId", wardenHostelId);

    payload.append("pocContact", formData.contact);
    payload.append("city", formData.city);
    payload.append("address", formData.address);
    payload.append("description", formData.description);

    if (formData.mapLink) {
      payload.append("mapLink", formData.mapLink);
    }

    if (formData.image) {
      payload.append("hotelImage", formData.image);
    }

    try {
      // Service function call
      await updateWardenProfile(wardenHostelId, payload);

      toast.success("Hostel Updated Successfully!");
      setVisible(false);
      fetchHostelDetails();
    } catch (error) {
      console.error("Update error:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Update failed");
    }
  };

  // --- 5. RENDER ---
  if (isLoading) {
    return (
      <CContainer fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <CSpinner color="primary" />
          <p className="mt-3">Loading hostel details...</p>
        </div>
      </CContainer>
    );
  }

  if (!hotelData?.hotel) {
    return (
      <CContainer fluid className="text-center mt-5">
        <h3>No Hostel Assigned. Contact Admin.</h3>
      </CContainer>
    );
  }

  const { hotel, roomList } = hotelData;
  const overallOccupancy = calculateOverallOccupancy();

  return (
    <CContainer fluid className="px-lg-4">

      {/* --- HEADER --- */}
      <CCard className="shadow-sm border-0 mb-4">
        <CCardBody className="p-3 p-sm-4">
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
            <div className="d-flex align-items-center">
              <div className="bg-light p-3 rounded me-3" style={{ flexShrink: 0 }}>
                <CIcon icon={cilBuilding} size="xl" className="text-primary" />
              </div>
              <div>
                <h4 className="mb-0 fw-bold">{hotel.name}</h4>
                <small className="text-muted">Managed by: {hotel.pocName || user?.name}</small>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2 w-100 w-md-auto">
              {roomList && roomList.length > 0 && (
                <CButton
                  color="success"
                  variant="outline"
                  onClick={() => exportToExcel(roomList, `${hotel.name}_rooms`)}
                  className="d-flex align-items-center gap-2 flex-grow-1 flex-md-grow-0 justify-content-center py-2"
                >
                  <CIcon icon={cilCloudDownload} />
                  <span className="d-none d-sm-inline">Export Data</span>
                  <span className="d-inline d-sm-none">Export</span>
                </CButton>
              )}
              <CButton
                color="primary"
                onClick={handleEdit}
                className="d-flex align-items-center gap-2 flex-grow-1 flex-md-grow-0 justify-content-center py-2"
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                }}
              >
                <CIcon icon={cilPencil} />
                Edit Details
              </CButton>
            </div>
          </div>
        </CCardBody>
      </CCard>

      <CRow className="g-3 g-lg-4">
        {/* --- LEFT COLUMN: OVERVIEW & CONTACT --- */}
        <CCol xs={12} lg={8}>

          {/* Hostel Overview */}
          <CCard className="shadow-sm mb-3 mb-lg-4 border-0">
            <CCardBody className="p-3 p-sm-4">
              <h5 className="mb-3 mb-sm-4 d-flex align-items-center fw-bold text-secondary">
                <CIcon icon={cilChart} className="me-2" />
                Hostel Overview
              </h5>

              <CRow className="g-3 mb-3">
                <CCol xs={6}>
                  <div className="p-3 bg-light rounded">
                    <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: "0.7rem" }}>Total Capacity</small>
                    <h3 className="mb-0 text-primary">{hotel.totalRoom || hotel.totalRooms || 0} <span className="d-none d-sm-inline">Rooms</span></h3>
                  </div>
                </CCol>
                <CCol xs={6}>
                  <div className="p-3 bg-light rounded">
                    <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: "0.7rem" }}>Current Occupancy</small>
                    <div className="d-flex align-items-center flex-wrap gap-1">
                      <h3 className="mb-0">{overallOccupancy}%</h3>
                      <CBadge color={getOccupancyBadgeColor(overallOccupancy)} className="d-none d-sm-inline">
                        {overallOccupancy < 30 ? "Low" : overallOccupancy < 80 ? "Medium" : "Full"}
                      </CBadge>
                    </div>
                  </div>
                </CCol>
              </CRow>

              <div>
                <small className="text-muted">Occupancy Visual</small>
                <CProgress className="mt-1" height={10}>
                  <CProgressBar value={overallOccupancy} color={getOccupancyBadgeColor(overallOccupancy)} />
                </CProgress>
              </div>
            </CCardBody>
          </CCard>

          {/* Contact Info */}
          <CCard className="shadow-sm mb-3 mb-lg-4 border-0">
            <CCardBody className="p-3 p-sm-4">
              <h5 className="mb-3 mb-sm-4 d-flex align-items-center fw-bold text-secondary">
                <CIcon icon={cilUser} className="me-2" />
                Contact Information
              </h5>
              <CRow className="g-3">
                <CCol xs={12} sm={6}>
                  <div className="p-3 border rounded">
                    <small className="text-muted d-block">POC Name</small>
                    <p className="mb-0 fw-semibold">{hotel.pocName || "N/A"}</p>
                  </div>
                </CCol>
                <CCol xs={12} sm={6}>
                  <div className="p-3 border rounded">
                    <small className="text-muted d-block">POC Contact</small>
                    <p className="mb-0 fw-semibold text-primary">
                      {formatPhoneNumber(hotel.pocContact)}
                    </p>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>

        {/* --- RIGHT COLUMN: LOCATION --- */}
        <CCol xs={12} lg={4}>
          <CCard className="shadow-sm mb-3 mb-lg-4 border-0">
            <CCardBody className="p-3 p-sm-4">
              <h5 className="mb-3 mb-sm-4 d-flex align-items-center fw-bold text-secondary">
                <CIcon icon={cilLocationPin} className="me-2" />
                Location
              </h5>

              <div className="mb-3 mb-sm-4">
                <small className="text-muted d-block">Full Address</small>
                <p className="mb-2 fw-semibold fs-6 fs-sm-5">{hotel.address || "N/A"}</p>
              </div>

              <CRow className="g-2 g-sm-3">
                <CCol xs={6}>
                  <div className="p-2 bg-light rounded">
                    <small className="text-muted d-block" style={{ fontSize: "0.75rem" }}>City</small>
                    <p className="mb-0 fw-bold small">{hotel.city || "N/A"}</p>
                  </div>
                </CCol>
                <CCol xs={6}>
                  <div className="p-2 bg-light rounded">
                    <small className="text-muted d-block" style={{ fontSize: "0.75rem" }}>Pincode</small>
                    <p className="mb-0 fw-bold small">{hotel.pincode || "N/A"}</p>
                  </div>
                </CCol>
                <CCol xs={6}>
                  <div className="p-2 bg-light rounded">
                    <small className="text-muted d-block" style={{ fontSize: "0.75rem" }}>State</small>
                    <p className="mb-0 fw-bold small">{hotel.state_name || "N/A"}</p>
                  </div>
                </CCol>
                <CCol xs={6}>
                  <div className="p-2 bg-light rounded">
                    <small className="text-muted d-block" style={{ fontSize: "0.75rem" }}>District</small>
                    <p className="mb-0 fw-bold small">{hotel.district_name || "N/A"}</p>
                  </div>
                </CCol>
              </CRow>

              {hotel.mapLink && (
                <div className="mt-3 mt-sm-4">
                  <CButton
                    color="primary"
                    variant="outline"
                    className="w-100 py-2"
                    href={hotel.mapLink}
                    target="_blank"
                  >
                    <CIcon icon={cilMap} className="me-2" />
                    Open in Google Maps
                  </CButton>
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* --- ROOM DETAILS TABLE --- */}
      {roomList && roomList.length > 0 && (
        <CCard className="shadow-sm border-0 mb-4">
          <CCardBody className="p-3 p-sm-4">
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-3 gap-2">
              <h5 className="mb-0 d-flex align-items-center fw-bold">
                <CIcon icon={cilBed} className="me-2 text-primary" />
                Room Occupancy
                <CBadge color="primary" className="ms-2 rounded-pill">
                  {roomList.length}
                </CBadge>
              </h5>
            </div>

            {/* Desktop Table */}
            <div className="d-none d-md-block table-responsive rounded border">
              <CTable hover responsive className="mb-0 align-middle">
                <CTableHead style={{ backgroundColor: "#f8f9fc" }}>
                  <CTableRow>
                    <CTableHeaderCell className="py-3 ps-3 text-muted small fw-semibold text-uppercase" style={{ width: "50px" }}>#</CTableHeaderCell>
                    <CTableHeaderCell className="py-3 text-muted small fw-semibold text-uppercase">Room Name</CTableHeaderCell>
                    <CTableHeaderCell className="py-3 text-center text-muted small fw-semibold text-uppercase" style={{ width: "90px" }}>Capacity</CTableHeaderCell>
                    <CTableHeaderCell className="py-3 text-center text-muted small fw-semibold text-uppercase" style={{ width: "140px" }}>Occupied / Available</CTableHeaderCell>
                    <CTableHeaderCell className="py-3 text-center text-muted small fw-semibold text-uppercase" style={{ width: "25%" }}>Occupancy Rate</CTableHeaderCell>
                    <CTableHeaderCell className="py-3 text-center text-muted small fw-semibold text-uppercase" style={{ width: "100px" }}>Status</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {roomList.map((room, index) => {
                    const max = parseInt(room.maxCapcity || room.totalBeds) || 1;
                    let occupied = 0;
                    if (room.bookedRooms !== undefined) occupied = room.bookedRooms;
                    else occupied = (max - (parseInt(room.available) || 0));

                    const available = max - occupied;
                    const occupancyPercentage = Math.round((occupied / max) * 100);

                    return (
                      <CTableRow key={index}>
                        <CTableDataCell className="ps-3 text-muted">{index + 1}</CTableDataCell>
                        <CTableDataCell>
                          <div className="fw-medium text-dark">{room.roomName || `Room ${index + 1}`}</div>
                          <small className="text-muted">{room.title || "Standard Room"}</small>
                        </CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CBadge color="info" shape="rounded-pill" className="px-3 py-1">{max}</CBadge>
                        </CTableDataCell>
                        <CTableDataCell className="text-center">
                          <span className="text-danger fw-bold">{occupied}</span>
                          <span className="text-muted mx-1">/</span>
                          <span className="text-success fw-bold">{available}</span>
                        </CTableDataCell>
                        <CTableDataCell>
                          <div className="d-flex align-items-center">
                            <CProgress className="flex-grow-1 me-2" height={6}>
                              <CProgressBar
                                value={occupancyPercentage}
                                color={getOccupancyBadgeColor(occupancyPercentage)}
                              />
                            </CProgress>
                            <span className="small fw-bold text-muted">{occupancyPercentage}%</span>
                          </div>
                        </CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CBadge color={getOccupancyBadgeColor(occupancyPercentage)} className="px-2 py-1" style={{ borderRadius: "20px" }}>
                            {occupancyPercentage < 100 ? "Available" : "Full"}
                          </CBadge>
                        </CTableDataCell>
                      </CTableRow>
                    );
                  })}
                </CTableBody>
              </CTable>
            </div>

            {/* Mobile Card View */}
            <div className="d-block d-md-none">
              {roomList.map((room, index) => {
                const max = parseInt(room.maxCapcity || room.totalBeds) || 1;
                let occupied = 0;
                if (room.bookedRooms !== undefined) occupied = room.bookedRooms;
                else occupied = (max - (parseInt(room.available) || 0));
                const available = max - occupied;
                const occupancyPercentage = Math.round((occupied / max) * 100);

                return (
                  <CCard key={index} className="mb-2 border shadow-sm">
                    <CCardBody className="py-3 px-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="text-muted small">#{index + 1}</span>
                            <h6 className="mb-0 fw-semibold">{room.roomName || `Room ${index + 1}`}</h6>
                          </div>
                          <small className="text-muted">{room.title || "Standard Room"}</small>
                        </div>
                        <CBadge color={getOccupancyBadgeColor(occupancyPercentage)} className="px-2 py-1" style={{ borderRadius: "20px", fontSize: "0.7rem" }}>
                          {occupancyPercentage < 100 ? "Available" : "Full"}
                        </CBadge>
                      </div>
                      <div className="d-flex gap-3 mb-2">
                        <div>
                          <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>Capacity</small>
                          <span className="fw-bold">{max}</span>
                        </div>
                        <div>
                          <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>Occupied</small>
                          <span className="fw-bold text-danger">{occupied}</span>
                        </div>
                        <div>
                          <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>Available</small>
                          <span className="fw-bold text-success">{available}</span>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <CProgress className="flex-grow-1 me-2" height={6}>
                          <CProgressBar value={occupancyPercentage} color={getOccupancyBadgeColor(occupancyPercentage)} />
                        </CProgress>
                        <span className="small fw-bold text-muted">{occupancyPercentage}%</span>
                      </div>
                    </CCardBody>
                  </CCard>
                );
              })}
            </div>
          </CCardBody>
        </CCard>
      )}

      {/* --- EDIT MODAL --- */}
      <CModal visible={visible} onClose={() => setVisible(false)} alignment="center">
        <CModalHeader
          className="border-0 pb-0"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <CModalTitle className="fw-bold text-white">Update Hostel Details</CModalTitle>
        </CModalHeader>
        <CModalBody className="p-3 p-sm-4 pt-4">
          <div className="mb-3">
            <CFormLabel className="fw-medium">Hostel Name (Read-Only)</CFormLabel>
            <CFormInput value={hotel.name} disabled className="bg-light py-2" />
          </div>
          <CRow className="g-3 mb-3">
            <CCol xs={12} sm={6}>
              <CFormLabel className="fw-medium">Contact Number</CFormLabel>
              <CFormInput name="contact" value={formData.contact} onChange={handleInputChange} type="tel" maxLength={10} placeholder="Enter 10 digit Mobile No." className="py-2" />
            </CCol>
            <CCol xs={12} sm={6}>
              <CFormLabel className="fw-medium">City</CFormLabel>
              <CFormInput name="city" value={formData.city} onChange={handleInputChange} className="py-2" />
            </CCol>
          </CRow>
          <div className="mb-3">
            <CFormLabel className="fw-medium">Address</CFormLabel>
            <CFormInput name="address" value={formData.address} onChange={handleInputChange} className="py-2" />
          </div>
          <div className="mb-3">
            <CFormLabel className="fw-medium">Description</CFormLabel>
            <CFormTextarea name="description" rows={3} value={formData.description} onChange={handleInputChange} />
          </div>
        </CModalBody>
        <CModalFooter className="border-0 pt-0 d-flex flex-column flex-sm-row gap-2">
          <CButton color="secondary" variant="outline" onClick={() => setVisible(false)} className="px-4 py-2 w-100 w-sm-auto">Cancel</CButton>
          <CButton
            color="primary"
            onClick={handleSubmit}
            className="px-4 py-2 w-100 w-sm-auto"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
            }}
          >
            Save Changes
          </CButton>
        </CModalFooter>
      </CModal>

    </CContainer>
  );
}